import React, { useState, useEffect } from 'react';
import AdminLayout from '../../components/AdminLayout';
import SafeIcon from '../../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { FiSend, FiSmartphone, FiCheck, FiHistory } = FiIcons;

const Notifications = () => {
  const [title, setTitle] = useState('');
  const [message, setMessage] = useState('');
  const [audience, setAudience] = useState('all');
  const [isSent, setIsSent] = useState(false);
  const [history, setHistory] = useState([]);

  useEffect(() => {
    const storedHistory = localStorage.getItem('ramirez_push_history');
    if (storedHistory) setHistory(JSON.parse(storedHistory));
  }, []);

  const handleSend = (e) => {
    e.preventDefault();
    
    setIsSent(true);
    
    // Save to history
    const newEntry = {
        id: Date.now(),
        date: new Date().toLocaleDateString(),
        time: new Date().toLocaleTimeString(),
        title: title,
        message: message,
        audience: audience
    };
    const updatedHistory = [newEntry, ...history];
    setHistory(updatedHistory);
    localStorage.setItem('ramirez_push_history', JSON.stringify(updatedHistory));

    setTimeout(() => {
        setIsSent(false);
        setTitle('');
        setMessage('');
    }, 3000);
  };

  return (
    <AdminLayout>
      <div className="mb-8">
        <h1 className="text-2xl font-bold text-white mb-2">Push Notifications</h1>
        <p className="text-gray-400">Send alerts directly to your customers' mobile devices.</p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        {/* Compose Section */}
        <div className="space-y-8">
            <div className="bg-gray-800 border border-gray-700 rounded-xl p-6">
            <h2 className="text-lg font-bold text-white mb-6 flex items-center gap-2">
                <SafeIcon icon={FiSend} className="text-blue-500" />
                Compose Message
            </h2>
            <form onSubmit={handleSend} className="space-y-6">
                <div>
                <label className="block text-gray-300 text-sm font-medium mb-2">Target Audience</label>
                <select 
                    value={audience}
                    onChange={(e) => setAudience(e.target.value)}
                    className="w-full bg-gray-700 border border-gray-600 text-white rounded-lg px-4 py-3 focus:ring-2 focus:ring-blue-500 outline-none"
                >
                    <option value="all">All Users</option>
                    <option value="reminders">Reminder Subscribers Only</option>
                    <option value="ontario">Ontario Region Only</option>
                </select>
                </div>
                <div>
                <label className="block text-gray-300 text-sm font-medium mb-2">Notification Title</label>
                <input 
                    type="text" 
                    value={title}
                    onChange={(e) => setTitle(e.target.value)}
                    placeholder="e.g., Compliance Deadline Approaching"
                    className="w-full bg-gray-700 border border-gray-600 text-white rounded-lg px-4 py-3 focus:ring-2 focus:ring-blue-500 outline-none"
                    required
                />
                </div>
                <div>
                <label className="block text-gray-300 text-sm font-medium mb-2">Message Body</label>
                <textarea 
                    value={message}
                    onChange={(e) => setMessage(e.target.value)}
                    placeholder="Enter your message here..."
                    rows="4"
                    className="w-full bg-gray-700 border border-gray-600 text-white rounded-lg px-4 py-3 focus:ring-2 focus:ring-blue-500 outline-none"
                    required
                ></textarea>
                <p className="text-xs text-gray-500 mt-2">Keep it short and clear. Max 120 characters recommended.</p>
                </div>
                <button 
                type="submit" 
                className="w-full bg-blue-600 text-white font-bold py-3 rounded-lg hover:bg-blue-700 transition-colors flex items-center justify-center gap-2"
                >
                {isSent ? (
                    <>
                    <SafeIcon icon={FiCheck} />
                    <span>Sent Successfully!</span>
                    </>
                ) : (
                    <>
                    <SafeIcon icon={FiSend} />
                    <span>Send Notification</span>
                    </>
                )}
                </button>
            </form>
            </div>

             <div className="bg-gray-800 border border-gray-700 rounded-xl p-6 shadow-xl">
                <h2 className="text-lg font-bold text-white mb-4 flex items-center gap-2">
                    <SafeIcon icon={FiHistory} className="text-gray-400" />
                    Sent History
                </h2>
                <div className="space-y-4 max-h-60 overflow-y-auto">
                    {history.length === 0 ? (
                        <p className="text-gray-500 text-sm text-center py-4">No notifications sent yet.</p>
                    ) : (
                        history.map(item => (
                            <div key={item.id} className="border-b border-gray-700 pb-3 last:border-0 last:pb-0">
                                <div className="flex justify-between items-center mb-1">
                                    <span className="text-xs text-gray-400">{item.date} {item.time}</span>
                                </div>
                                <p className="font-bold text-sm text-white">{item.title}</p>
                                <p className="text-sm text-gray-400 truncate">{item.message}</p>
                            </div>
                        ))
                    )}
                </div>
            </div>
        </div>

        {/* Preview Section */}
        <div>
          <h2 className="text-lg font-bold text-white mb-6">Mobile Preview</h2>
          <div className="flex justify-center">
            <div className="w-[300px] h-[600px] bg-black rounded-[3rem] border-8 border-gray-800 relative overflow-hidden shadow-2xl">
              {/* Notch */}
              <div className="absolute top-0 left-1/2 transform -translate-x-1/2 w-32 h-6 bg-gray-800 rounded-b-xl z-20"></div>
              {/* Screen Content */}
              <div className="w-full h-full bg-gray-900 pt-12 px-4 relative">
                <div className="flex justify-between items-center text-gray-400 text-xs mb-8 px-2">
                  <span>9:41</span>
                  <div className="flex gap-1">
                    <SafeIcon icon={FiSmartphone} />
                    <span>5G</span>
                  </div>
                </div>
                {/* Notification Card */}
                <div className="bg-gray-800/90 backdrop-blur-md rounded-2xl p-4 shadow-lg border border-gray-700 transform transition-all duration-500 translate-y-0">
                  <div className="flex items-start gap-3">
                    <div className="w-10 h-10 rounded-lg bg-blue-600 flex items-center justify-center shrink-0">
                      <SafeIcon icon={FiIcons.FiTruck} className="text-white text-xl" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex justify-between items-start">
                        <h4 className="font-bold text-white text-sm truncate">Ramirez Mobile</h4>
                        <span className="text-[10px] text-gray-500">now</span>
                      </div>
                      <p className="text-white font-medium text-sm mt-1">
                        {title || "Notification Title"}
                      </p>
                      <p className="text-gray-400 text-xs mt-1 leading-relaxed">
                        {message || "Your notification message will appear here exactly as the user sees it."}
                      </p>
                    </div>
                  </div>
                </div>
                {/* Background App Hint */}
                <div className="mt-8 opacity-30 pointer-events-none">
                  <div className="h-32 bg-gray-800 rounded-xl mb-4"></div>
                  <div className="h-32 bg-gray-800 rounded-xl mb-4"></div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </AdminLayout>
  );
};

export default Notifications;